import { TextButton } from '@creditornot/cb-button';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { MenuAccordionItem, MenuCheckboxItem, MenuRadioItem } from '@creditornot/cb-menu';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { SelectableItem, SelectableItemsGroup } from './types';

export type SelectableAccordionProps = {
  items: SelectableItemsGroup[];
  expandedByDefault?: boolean;
  headerLabel?: string;
  onItemClick: ({
    groupId,
    type,
    option,
  }: {
    groupId: string;
    type: SelectableItemsGroup['type'];
    option: SelectableItem;
  }) => void;
  onGroupClick: (groupId: string) => void;
  onClearAllClick?: () => void;
  selectedIds: string[];
  expandedGroups: string[];
  expandAllGroups: () => void;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
  min-width: 344px;
`;

const Header = styled.div`
  ${typographyCss.Body3};
  color: ${cbSemanticCssVars.textSecondary};
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding: 8px 8px 14px;
`;

const StyledTextButton = styled(TextButton)`
  padding: 12px;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

export const SelectableAccordion = ({
  items,
  expandedByDefault = false,
  onItemClick,
  onClearAllClick,
  onGroupClick,
  expandedGroups = [],
  expandAllGroups,
  selectedIds,
  headerLabel,
}: SelectableAccordionProps) => {
  useEffect(() => {
    if (expandedByDefault) {
      expandAllGroups();
    }
  }, [expandAllGroups, expandedByDefault]);

  return (
    <Root>
      {!!headerLabel && <Header>{headerLabel}</Header>}
      {items
        .filter(({ hide }) => !hide)
        .map(({ groupId, groupLabel, type, options }) => (
          <MenuAccordionItem
            key={groupId}
            aria-label={groupLabel}
            accordionContent={
              <>
                {type === 'checkbox' &&
                  options
                    .filter(({ hide }) => !hide)
                    .map(option => (
                      <MenuCheckboxItem
                        aria-label={option.label}
                        key={option.id}
                        checked={!!selectedIds?.includes(option.id)}
                        disabled={option.disabled}
                        onClick={() => onItemClick({ groupId, type, option })}
                      >
                        <FormattedMessage id={option.label} defaultMessage={option.label} />
                      </MenuCheckboxItem>
                    ))}
                {type === 'radio' &&
                  options.map(option => (
                    <MenuRadioItem
                      aria-label={option.label}
                      key={option.id}
                      checked={!!selectedIds?.includes(option.id)}
                      disabled={option.disabled}
                      onClick={() => onItemClick({ groupId, type, option })}
                    >
                      <FormattedMessage id={option.label} defaultMessage={option.label} />
                    </MenuRadioItem>
                  ))}
              </>
            }
            caretPosition="right"
            onClick={() => onGroupClick(groupId)}
            open={expandedGroups.includes(groupId)}
          >
            <FormattedMessage id={groupLabel} />
          </MenuAccordionItem>
        ))}
      {!!onClearAllClick && (
        <StyledTextButton onClick={onClearAllClick}>
          <FormattedMessage id="shared-menu.clear-all-button-label" />
        </StyledTextButton>
      )}
    </Root>
  );
};
